import React, { useEffect } from 'react'
import { useState } from 'react';
import { getColorBasedOnDegree } from '../../utils/note-color';
import './guitar.scss'

const Guitar = ({ tuning, capo, activeNotes, updateNotes, clickToAddAndRemove, hideNotesAfter12th = false, hideNoteNames = false, vertical = false }) => {
    const frets = 24;
    const fretMarkers = [3, 5, 7, 9, 15, 17, 19, 21, 24];
    const [fretBoard, setFretBoard] = useState([]);
    const [stringNotes, setStringNotes] = React.useState([]);

    useEffect(() => {
        generateFretBoard();
        updateStringNotes();
    }, [tuning, activeNotes]);

    const generateFretBoard = () => {
        const newFretBoard = tuning.map(string =>
            Array.from({ length: frets + 1 }, (_, fret) => noteAtFret(string, fret))
        );
        setFretBoard(newFretBoard);
    };

    const updateStringNotes = () => {
        setStringNotes(tuning.map(note => note.toUpperCase()));
    };

    const parseActiveNotes = () => {
        const globalNotes = [];
        const stringSpecificNotes = {};

        activeNotes.forEach(noteEntry => {
            if (typeof noteEntry === 'string') {
                globalNotes.push(noteEntry);
            } else if (typeof noteEntry === 'object' && noteEntry.note && noteEntry.string !== undefined) {
                if (!stringSpecificNotes[noteEntry.string]) {
                    stringSpecificNotes[noteEntry.string] = [];
                }
                stringSpecificNotes[noteEntry.string].push(noteEntry.note);
            }
        });

        return { globalNotes, stringSpecificNotes };
    };

    const noteIsPresent = (note, fret, stringIndex) => {
        const { globalNotes, stringSpecificNotes } = parseActiveNotes();

        if (capo && fret < capo) {
            return false;
        }

        if (hideNotesAfter12th && fret > 12) {
            return false;
        }

        const isGlobalNote = globalNotes.includes(note);

        const isStringSpecificNote = stringSpecificNotes[stringIndex] &&
            stringSpecificNotes[stringIndex].includes(note);

        return isGlobalNote || isStringSpecificNote;
    }

    const isCapo = (index) => {
        return capo === index;
    }

    const noteAtFret = (openNote, fret) => {
        let startIndex = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'].indexOf(openNote);

        if (startIndex === -1) {
            console.error("Invalid open note:", openNote);
            return "";
        }

        let noteIndex = (startIndex + fret) % 12;

        return ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'][noteIndex]
    };

    const isFretMarker = (fret, string) => {
        return fretMarkers.includes(fret) && string === 2;
    };

    const setCapoStyles = (string, isVertical = false) => {
        if (isVertical) {
            if (string === 0) {
                return { borderRadius: '50px 0 0 50px', borderLeft: 'solid 2px #0B6BCB' }
            } else if (string === (tuning.length - 1)) {
                return { borderRadius: '0 50px 50px 0', borderRight: 'solid 2px #0B6BCB' }
            }
        } else {
            if (string === 0) {
                return { borderRadius: '50px 50px 0 0', borderTop: 'solid 2px #0B6BCB' }
            } else if (string === (tuning.length - 1)) {
                return { borderRadius: '0 0 50px 50px', borderBottom: 'solid 2px #0B6BCB' }
            }
        }
    }

    const handleNoteClick = (note, stringIndex) => {
        if (!clickToAddAndRemove) {
            return;
        }
        const { globalNotes, stringSpecificNotes } = parseActiveNotes();

        if (globalNotes.includes(note)) {
            const newActiveNotes = activeNotes.filter(entry =>
                typeof entry === 'string' ? entry !== note : true
            );
            updateNotes(newActiveNotes);
        } else {
            const newActiveNotes = [...activeNotes, note];
            updateNotes(newActiveNotes);
        }
    }

    const isNoteClickable = (note, stringIndex) => {
        if (!clickToAddAndRemove) return false;

        const { globalNotes, stringSpecificNotes } = parseActiveNotes();
        const isActive = globalNotes.includes(note) ||
            (stringSpecificNotes[stringIndex] && stringSpecificNotes[stringIndex].includes(note));

        return !isActive;
    };

    const renderHorizontal = () => (
        <div className="fret-board horizontal">
            <table>
                <tbody>
                    {fretBoard.map((strings, j) => (
                        <tr key={j}>
                            <td className="note-marker">{stringNotes[j]}</td>
                            {strings.map((note, i) => (
                                <td
                                    key={i}
                                    style={{
                                        '--border-width': (j + 1) * 0.5 + 'px',
                                        width: i === 0 ? '25px' : 80 - 1.5 * i + 'px',
                                        backgroundColor: i === 0 ? 'white' : '',
                                    }}
                                    className={` ${isNoteClickable(note, j) ? 'scale-finder-note' : ''}
                                    ${clickToAddAndRemove ? 'allow-hover' : ''}`}
                                >
                                    {(noteIsPresent(note, i, j) || clickToAddAndRemove) &&
                                        <div className='note-button-container'>
                                            <button
                                                tabIndex={clickToAddAndRemove ? 0 : -1}
                                                className={`note-button`}
                                                style={{ backgroundColor: getColorBasedOnDegree(note, activeNotes)}}
                                                onClick={() => handleNoteClick(note, j)}
                                            >{hideNoteNames ? '' : note}</button>
                                        </div>
                                    }
                                    {
                                        isCapo(i) &&
                                        <div className='capo-container'>
                                            <div className='capo-marker' style={setCapoStyles(j)}></div>
                                        </div>
                                    }
                                    {isFretMarker(i, j) && <div className="single-marker fret-marker"></div>}
                                    {i === 12 && j === 2 && (
                                        <div className="double-marker">
                                            <div className="fret-marker"></div>
                                            <div className="fret-marker"></div>
                                        </div>
                                    )}
                                </td>
                            ))}
                        </tr>
                    ))}
                    <tr className="fret-labels">
                        <td></td>
                        {fretBoard.length > 0 && fretBoard[0].map((_, i) => <td key={i}>{i}</td>)}
                    </tr>
                </tbody>
            </table>
        </div>
    );

    const renderVertical = () => (
        <div className="fret-board vertical">
            <table>
                <tbody>
                    {fretBoard.length > 0 && fretBoard[0].map((_, fretIndex) => (
                        <tr key={fretIndex}>
                            <td className="fret-number">{fretIndex}</td>
                            {fretBoard.map((strings, stringIndex) => {
                                const note = strings[fretIndex];
                                return (
                                    <td
                                        key={stringIndex}
                                        style={{
                                            '--border-width': (stringIndex + 1) * 0.5 + 'px',
                                            height: fretIndex === 0 ? '25px' : 80 - 1.5 * fretIndex + 'px',
                                            backgroundColor: fretIndex === 0 ? 'white' : '',
                                            minWidth: '30px'
                                        }}
                                        className={` ${isNoteClickable(note, stringIndex) ? 'scale-finder-note' : ''}
                                        ${clickToAddAndRemove ? 'allow-hover' : ''}`}
                                    >
                                        {(noteIsPresent(note, fretIndex, stringIndex) || clickToAddAndRemove) &&
                                            <div className='note-button-container'>
                                                <button
                                                    tabIndex={clickToAddAndRemove ? 0 : -1}
                                                    className={`note-button`}
                                                    style={{ backgroundColor: getColorBasedOnDegree(note, activeNotes) }}
                                                    onClick={() => handleNoteClick(note, stringIndex)}
                                                >{hideNoteNames ? '' : note}</button>
                                            </div>
                                        }
                                        {
                                            isCapo(fretIndex) &&
                                            <div className='capo-container'>
                                                <div className='capo-marker' style={setCapoStyles(stringIndex, true)}></div>
                                            </div>
                                        }
                                        {isFretMarker(fretIndex, stringIndex) && <div className="single-marker fret-marker"></div>}
                                        {fretIndex === 12 && stringIndex === 2 && (
                                            <div className="double-marker">
                                                <div className="fret-marker"></div>
                                                <div className="fret-marker"></div>
                                            </div>
                                        )}
                                    </td>
                                );
                            })}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );

    return vertical ? renderVertical() : renderHorizontal();
}

export default Guitar